import type { PerimeterXEnforcerChallengeWithBlockedUrl } from '@change-corgi/core/perimeterx';

export type Options = {
	pxResponse: PerimeterXEnforcerChallengeWithBlockedUrl;
};

export type Result = {
	success: boolean;
};

export type CaptchaHostState =
	| { status: 'idle' }
	| { status: 'pending'; pxResponse: PerimeterXEnforcerChallengeWithBlockedUrl; challengeId: number };

const IDLE_STATE: CaptchaHostState = { status: 'idle' };

export class CaptchaHostStore {
	private state: CaptchaHostState = IDLE_STATE;
	private resolver: ((success: boolean) => void) | null = null;
	private challengeCount = 0;
	private listeners = new Set<() => void>();

	private notify(): void {
		this.listeners.forEach((listener) => {
			listener();
		});
	}

	/**
	 * Resolves the pending `open()` promise with `success` and resets state back to idle. Any close
	 * without a vendor result settles `false`, so no awaiting caller can ever hang.
	 */
	private settle(success: boolean): void {
		const resolve = this.resolver;
		this.resolver = null;
		if (this.state.status !== 'idle') {
			this.state = IDLE_STATE;
			this.notify();
		}
		resolve?.(success);
	}

	subscribe(listener: () => void): () => void {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}

	getSnapshot(): CaptchaHostState {
		return this.state;
	}

	/**
	 * Returns the idle state for server-side rendering, since challenges only ever fire from client
	 * async handlers and the host therefore renders null on the server.
	 */
	// oxlint-disable-next-line class-methods-use-this
	getServerSnapshot(): CaptchaHostState {
		return IDLE_STATE;
	}

	/**
	 * Opens a captcha challenge for `pxResponse` and returns a promise that resolves once it is
	 * resolved or dismissed. A superseding call resolves the prior promise `false`, then opens
	 * fresh — the vendor requires exactly one `#px-captcha` on the page.
	 */
	async open({ pxResponse }: Options): Promise<boolean> {
		this.resolver?.(false);
		this.resolver = null;
		this.challengeCount += 1;
		this.state = { status: 'pending', pxResponse, challengeId: this.challengeCount };
		this.notify();
		// oxlint-disable-next-line promise/avoid-new
		return new Promise<boolean>((resolve) => {
			this.resolver = resolve;
		});
	}

	resolve(result: Result): void {
		this.settle(result.success);
	}

	/**
	 * Closes the current challenge without a vendor result, settling the pending promise as failed.
	 * Covers ESC, supersede, or host unmount — anything that closes without a vendor result.
	 */
	dismiss(): void {
		this.settle(false);
	}
}
